import { TERMINAL_RUN_STATUSES } from '../types/content.types';

/** Semantic tone, matching the shared design tokens (`bg-success-50` / `text-success`, …). */
export type StatusTone = 'info' | 'success' | 'warning' | 'error' | 'muted';

/**
 * Whether a run status is one the run can never move out of.
 *
 * Takes a plain string, not the `RunStatus` union: statuses come back out of
 * ClickHouse and a row written by a newer worker can legitimately hold a value
 * this build has never heard of. An unknown status counts as NOT terminal, so
 * the UI keeps polling rather than freezing a live run.
 */
export function isTerminalRunStatus(status: string): boolean {
  return (TERMINAL_RUN_STATUSES as readonly string[]).includes(status);
}

/** Tone for a run status. Unknown values fall back to muted rather than throwing. */
export function runStatusTone(status: string): StatusTone {
  switch (status) {
    case 'COMPLETED':
      return 'success';
    case 'FAILED':
      return 'error';
    case 'PAUSED':
      return 'warning';
    case 'PENDING':
    case 'PLANNING':
    case 'GENERATING':
      return 'info';
    case 'CANCELLED':
      return 'muted';
    default:
      return 'muted';
  }
}

/** Tone for a batch status. Same mapping shape as `runStatusTone`. */
export function batchStatusTone(status: string): StatusTone {
  switch (status) {
    case 'COMPLETED':
      return 'success';
    case 'FAILED':
      return 'error';
    case 'RUNNING':
      return 'info';
    case 'PENDING':
      return 'warning';
    case 'SKIPPED':
    case 'CANCELLED':
      return 'muted';
    default:
      return 'muted';
  }
}

/**
 * Tone for a taxonomy job status — Temporal's execution statuses, not the run
 * vocabulary (`CANCELED` with one L, `TIMED_OUT`, `NOT_STARTED`).
 */
export function taxonomyJobStatusTone(status: string): StatusTone {
  switch (status) {
    case 'COMPLETED':
      return 'success';
    case 'RUNNING':
      return 'info';
    case 'FAILED':
    case 'TIMED_OUT':
      return 'error';
    // Stopped by a person or the engine rather than by the job's own error.
    case 'CANCELED':
    case 'TERMINATED':
      return 'warning';
    default:
      return 'muted';
  }
}

/** Run status in words a non-technical user recognises ("Running", not "Generating"). */
const RUN_STATUS_LABEL: Record<string, string> = {
  PENDING: 'Waiting to start',
  PLANNING: 'Getting ready',
  GENERATING: 'Running',
  COMPLETED: 'Finished',
  FAILED: 'Failed',
  PAUSED: 'Paused',
  CANCELLED: 'Cancelled',
};

/** Plain label for a run status; unknown values from a newer worker are humanised. */
export function runStatusLabel(status: string): string {
  return RUN_STATUS_LABEL[status] ?? humanizeEnum(status);
}

/** A run that is actively moving — drives the pulsing dot on the status chip. */
export function isRunInFlight(status: string): boolean {
  return status === 'PENDING' || status === 'PLANNING' || status === 'GENERATING';
}

/**
 * `SCREAMING_SNAKE` → `Screaming snake`, for enum values the service sends raw.
 * Leaves an unrecognised value readable rather than dropping it.
 */
export function humanizeEnum(value: string): string {
  if (!value) return '—';
  const lower = value.replaceAll('_', ' ').toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/**
 * Which control actions are offered for a run in this status.
 *
 * Derived from the status rather than always showing all three: pausing a
 * completed run is a request the service would reject, and offering it invites
 * the click.
 */
export function availableRunActions(status: string): {
  canPause: boolean;
  canResume: boolean;
  canCancel: boolean;
} {
  if (isTerminalRunStatus(status)) {
    return { canPause: false, canResume: false, canCancel: false };
  }
  const paused = status === 'PAUSED';
  return { canPause: !paused, canResume: paused, canCancel: true };
}

// ---------------------------------------------------------------------------
// Batch status — plain-language labels and meanings
// ---------------------------------------------------------------------------

/** What each batch status means, for the Batches tab legend and chip tooltips. */
const BATCH_STATUS_MEANING: Record<string, { label: string; meaning: string }> = {
  PENDING: {
    label: 'Waiting',
    meaning: 'Planned, but not started yet — it is queued behind the batches ahead of it.',
  },
  RUNNING: { label: 'Running', meaning: 'Writing content for its products right now.' },
  COMPLETED: {
    label: 'Finished',
    meaning: 'Finished. Individual products inside it can still have failed — see Problems.',
  },
  FAILED: {
    label: 'Failed',
    meaning: 'Stopped with an error before finishing; its error column says why.',
  },
  SKIPPED: {
    label: 'Skipped',
    meaning:
      'Never started because the run was cancelled first. Not an error — it is recorded so every planned batch is accounted for.',
  },
  CANCELLED: {
    label: 'Cancelled',
    meaning: 'Was running when the run was cancelled, and was stopped part-way.',
  },
};

/** Short label for a batch status ("Waiting" for PENDING); unknown values are humanised. */
export function batchStatusLabel(status: string): string {
  return BATCH_STATUS_MEANING[status]?.label ?? humanizeEnum(status);
}

/** One-sentence meaning of a batch status, or '' for a value this build does not know. */
export function batchStatusMeaning(status: string): string {
  return BATCH_STATUS_MEANING[status]?.meaning ?? '';
}

// ---------------------------------------------------------------------------
// Workflow engine vocabulary — history summaries and reset points
// ---------------------------------------------------------------------------

/**
 * Splits a history summary ("activity_type=fetch_products · activity_id=3")
 * into key/value pairs. A part with no `=` (a failure message) is keyed "message".
 */
export function parseEventSummary(summary: string): [string, string][] {
  if (!summary) return [];
  return summary.split(' · ').map((part) => {
    const eq = part.indexOf('=');
    // Keys are snake_case identifiers; anything else is free text that contains '='.
    if (eq > 0 && /^[a-z_]+$/.test(part.slice(0, eq))) {
      return [part.slice(0, eq), part.slice(eq + 1)];
    }
    return ['message', part];
  });
}

/** `ActivityTaskScheduled` → `Activity task scheduled`. */
export function humanizeEventType(eventType: string): string {
  if (!eventType) return '—';
  const words = eventType.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** `fetch_products` / `FetchProducts` → `fetch products`, for quoting a step name in prose. */
function stepName(raw: string): string {
  return raw
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replaceAll('_', ' ')
    .toLowerCase();
}

/**
 * Plain-words label for a reset point, from what the workflow did right after
 * it (`followedBy`). Resetting there re-runs that step and everything after it.
 */
export function describeResetPoint(followedBy: string): string {
  if (!followedBy) return 'The most recent point — nothing has happened after it yet';
  const pairs = Object.fromEntries(parseEventSummary(followedBy));
  if (pairs.activity_type) return `Just before the “${stepName(pairs.activity_type)}” step`;
  if (pairs.workflow_type) return `Just before starting a “${stepName(pairs.workflow_type)}” batch`;
  if (pairs.signal_name) return `Just before the “${pairs.signal_name}” signal arrived`;
  // No summary fields: followedBy is the next event's type name (CamelCase, no spaces).
  if (/^[A-Z][A-Za-z]+$/.test(followedBy)) {
    return `Just before: ${humanizeEventType(followedBy).toLowerCase()}`;
  }
  return `Just before: ${followedBy}`;
}

/**
 * Tone for what the workflow ENGINE reports (Temporal execution status). Same
 * vocabulary as taxonomy jobs, so it shares that mapping.
 */
export function workflowStatusTone(status: string): StatusTone {
  return taxonomyJobStatusTone(status);
}

/** Tone for one history event: failures/time-outs read as errors, completions as success. */
export function historyEventTone(eventType: string): StatusTone {
  if (/Failed|TimedOut|Terminated/.test(eventType)) return 'error';
  if (/Canceled|Cancelled/.test(eventType)) return 'warning';
  // Workflow-task bookkeeping completes constantly; only real steps earn green.
  if (eventType.includes('Completed') && !eventType.startsWith('WorkflowTask')) return 'success';
  return 'muted';
}
